import { useState, useEffect, useCallback } from "react";
import { Star, Users, TrendingUp, Timer, X, Send, Award, MessageSquarePlus, Loader2 } from "lucide-react";
import { api } from "../lib/apiClient";

/**
 * BetaTestersRegistry — Registre 100% public MongoDB.
 * Source de vérité unique : GET /api/feedbacks (aucun localStorage, aucun cache client).
 * Tous les visiteurs (desktop, mobile, incognito) voient exactement les mêmes chiffres et avis.
 */
const CASE_TYPES = [
  { key: "caution", label: "Dépôt de garantie", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "charges", label: "Régularisation charges", color: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "fps", label: "FPS · Stationnement", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "bail", label: "Bail · Litige locatif", color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { key: "autre", label: "Autre litige civil", color: "bg-slate-100 text-slate-700 border-slate-200" },
];
function caseMeta(key) {
  return CASE_TYPES.find((c) => c.key === key) || { key: "autre", label: "Non précisé", color: "bg-slate-100 text-slate-700 border-slate-200" };
}

export function BetaTestersRegistry({ autoOpen = false, onAutoOpenHandled }) {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [avg, setAvg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [caseType, setCaseType] = useState("caution");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/feedbacks");
      const data = r.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotalCount(Number(data.total_count) || 0);
      setAvg(Number(data.average_rating) || 0);
    } catch { setItems([]); setTotalCount(0); setAvg(0); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (autoOpen) {
      setOpenModal(true);
      if (onAutoOpenHandled) onAutoOpenHandled();
      setTimeout(() => {
        const el = document.querySelector('[data-testid="beta-registry-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [autoOpen, onAutoOpenHandled]);

  // Validation live — affiche exactement ce qui manque, mais le bouton reste cliquable
  const missing = [];
  if (rating === 0) missing.push("votre note (étoiles)");
  if (name.trim().length < 2) missing.push("votre prénom (2 car. minimum)");
  const commentLen = comment.trim().length;
  if (commentLen < 10) missing.push(`votre retour (encore ${10 - commentLen} caractère${10 - commentLen > 1 ? "s" : ""})`);
  const hasMissing = missing.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (hasMissing) {
      setError("Il manque : " + missing.join(" · "));
      return;
    }
    setSubmitting(true); setError("");
    try {
      await api.post("/feedbacks", {
        case_type: caseType,
        rating,
        comment: comment.trim(),
        author_name: name.trim(),
        city: city.trim(),
      });
      setSubmitted(true);
      setRating(0); setName(""); setCity(""); setComment(""); setCaseType("caution");
      await refresh();
      setTimeout(() => { setSubmitted(false); setOpenModal(false); }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Publication impossible. Vérifiez votre connexion et réessayez.");
    } finally { setSubmitting(false); }
  };

  const closeModal = () => { if (submitting) return; setOpenModal(false); setError(""); };

  return (
    <section className="mt-16 pt-16 pb-14 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-gradient-to-b from-slate-100 via-slate-50 to-white border-t-4 border-emerald-500" data-testid="beta-registry-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start gap-3 flex-wrap" data-testid="beta-registry-header">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-600 text-white text-[0.65rem] uppercase tracking-widest font-black shadow">
            <Award className="w-3 h-3" /> Registre indépendant
          </div>
        </div>
        <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#0F172A] mt-3 leading-tight" data-testid="beta-registry-title">
          Registre des Bêta-Testeurs & Retours Terrain
        </h2>
        <p className="text-sm sm:text-base text-slate-600 mt-2 max-w-3xl" data-testid="beta-registry-subtitle">
          Évaluations indépendantes issues des dossiers réels pré-instruits sur JurisFlash. Registre public partagé (MongoDB).
        </p>

        {/* BANDEAU TRACTION GLOBALE */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="beta-traction-banner">
          <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-sm flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0"><Users className="w-5 h-5 text-emerald-700" /></div>
            <div className="min-w-0">
              <div className="text-2xl sm:text-3xl font-heading font-black text-slate-900 leading-none" data-testid="beta-stat-count">{totalCount}</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest font-bold mt-1">{totalCount === 1 ? "dossier instruit" : "dossiers instruits"}</div>
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-sm flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0"><Star className="w-5 h-5 text-amber-600 fill-amber-500" /></div>
            <div className="min-w-0">
              {totalCount > 0 && avg != null ? (
                <>
                  <div className="text-2xl sm:text-3xl font-heading font-black text-slate-900 leading-none">
                    <span data-testid="beta-stat-avg">{avg.toFixed(1)}</span>
                    <span className="text-base font-bold text-slate-400"> / 5</span>
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest font-bold mt-1">note moyenne</div>
                </>
              ) : (
                <>
                  <div className="text-lg sm:text-xl font-heading font-black text-slate-700 leading-tight" data-testid="beta-stat-avg">Nouveau</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest font-bold mt-1">en cours d'évaluation</div>
                </>
              )}
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-sm flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0"><Timer className="w-5 h-5 text-sky-700" /></div>
            <div className="min-w-0">
              <div className="text-2xl sm:text-3xl font-heading font-black text-slate-900 leading-none" data-testid="beta-stat-gain">4h30</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest font-bold mt-1">gain moyen par litige</div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <span>Chaque retour terrain renforce la traction produit et alimente le registre.</span>
          </div>
          <button type="button" onClick={() => setOpenModal(true)} className="min-h-[48px] px-5 py-2.5 rounded-xl bg-white border-2 border-emerald-500 text-emerald-700 font-heading font-black text-sm hover:bg-emerald-50 transition-colors inline-flex items-center gap-2 shadow-sm" data-testid="beta-open-modal-btn">
            <MessageSquarePlus className="w-4 h-4" /> Vous avez testé un dossier ? Laisser votre avis
          </button>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="beta-cards-grid">
          {loading ? (
            <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="beta-loading">
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-emerald-600" />
              <div className="text-sm text-slate-500 mt-2">Chargement du registre…</div>
            </div>
          ) : items.length === 0 ? (
            <div className="md:col-span-2 rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50/40 p-8 text-center" data-testid="beta-empty">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3"><Award className="w-6 h-6 text-emerald-600" /></div>
              <div className="font-heading font-black text-lg text-slate-800">Soyez le premier à tester JurisFlash.</div>
              <div className="text-sm text-slate-600 mt-2 max-w-md mx-auto">Lancez une analyse en 47 s et enregistrez votre retour dans le registre.</div>
            </div>
          ) : items.map((it) => {
            const meta = caseMeta(it.case_type);
            const author = it.city ? `${it.author_name}, ${it.city}` : it.author_name;
            return (
              <article key={it.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`beta-card-${it.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <span className={`text-[0.65rem] uppercase tracking-widest font-black px-2.5 py-1 rounded-full border ${meta.color}`} data-testid={`beta-card-badge-${it.id}`}>
                    Cas : {meta.label}
                  </span>
                  <div className="flex gap-0.5" aria-label={`Note : ${it.rating} sur 5`}>
                    {[1,2,3,4,5].map((n) => (<Star key={n} className={`w-4 h-4 ${n <= it.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />))}
                  </div>
                </div>
                <p className="text-sm text-slate-700 mt-3 leading-relaxed whitespace-pre-wrap" data-testid={`beta-card-comment-${it.id}`}>“{it.comment}”</p>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="font-heading font-black text-slate-800" data-testid={`beta-card-author-${it.id}`}>{author}</span>
                  <span className="text-slate-400">{it.created_at ? new Date(it.created_at).toLocaleDateString("fr-FR") : ""}</span>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {openModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm" onClick={closeModal} data-testid="beta-modal">
          <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between">
              <div>
                <div className="j-label flex items-center gap-1.5"><MessageSquarePlus className="w-3 h-3" /> Nouveau retour</div>
                <h3 className="font-heading font-black text-xl text-slate-900">Publier votre avis de bêta-testeur</h3>
              </div>
              <button type="button" onClick={closeModal} disabled={submitting} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-40" aria-label="Fermer" data-testid="beta-modal-close">
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4" data-testid="beta-modal-form">
              <div>
                <label className="j-label mb-2 block">Type de litige testé</label>
                <div className="flex flex-wrap gap-2">
                  {CASE_TYPES.map((c) => (
                    <button key={c.key} type="button" onClick={() => setCaseType(c.key)} className={`min-h-[40px] px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border transition-colors ${caseType === c.key ? "bg-emerald-600 text-white border-emerald-600 shadow" : "bg-white border-slate-200 text-slate-700 hover:border-emerald-400"}`} data-testid={`beta-modal-case-${c.key}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="j-label mb-2 block">Votre note globale</label>
                <div className="flex gap-1" role="radiogroup" aria-label="Note globale sur 5 étoiles">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} type="button" onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} className="p-1 min-h-[44px] min-w-[44px] flex items-center justify-center hover:scale-110 transition-transform" aria-label={`${n} étoile${n > 1 ? "s" : ""}`} data-testid={`beta-modal-star-${n}`}>
                      <Star className={`w-7 h-7 transition-colors ${(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="j-label mb-1 block">Prénom (anonymisé)</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="ex : Karim M." maxLength={60} className="j-input" data-testid="beta-modal-name" />
                </div>
                <div>
                  <label className="j-label mb-1 block">Ville</label>
                  <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="ex : Lille" maxLength={60} className="j-input" data-testid="beta-modal-city" />
                </div>
              </div>
              <div>
                <label className="j-label mb-1 block">Votre retour</label>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Décrivez l'expérience de pré-instruction du dossier testé…" maxLength={500} rows={4} className="j-textarea" data-testid="beta-modal-comment" />
                <div className="text-xs text-slate-400 mt-1 text-right">{comment.length}/500</div>
              </div>
              {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800" data-testid="beta-modal-error">{error}</div>}
              {hasMissing && !error && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="beta-modal-helper">
                  Pour publier, il manque : <b>{missing.join(" · ")}</b>
                </div>
              )}
              <button type="submit" disabled={submitting} className="btn-emerald w-full min-h-[48px] disabled:opacity-50" data-testid="beta-modal-submit">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Publication…</> : <><Send className="w-4 h-4" /> {submitted ? "Avis publié ✓" : "Publier mon avis au registre"}</>}
              </button>
              {submitted && (
                <div className="text-sm text-emerald-700 font-semibold text-center" data-testid="beta-modal-submitted">
                  Merci pour votre retour ! Il est intégré au registre public.
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
